import { debounce } from 'lodash';
import analytics from '../utils/analytics';
import { useEffect, useRef, useCallback } from 'react';
import { sentryCaptureException } from '../utils/sentry/sentry-client';
import { AdditionalAttributes } from '../utils/analytics/types';

/**
 * Hook to track scroll depth analytics
 *
 * Tracks the maximum scroll depth reached on a page and sends analytics events
 * on each new maximum depth reached ("scrolled" action).
 *
 * Require `eventContainer` as input, since it maps to `source` and is required by the spec:
 * @see https://hello.atlassian.net/wiki/spaces/AAPT/pages/134329336/UI+Events
 * `action` and `actionSubject` are provided by the `event` and `eventComponent` values in the hook itself.
 */
export const useScrollDepthAnalytics = (
    eventProps: { eventContainer: string } & AdditionalAttributes
) => {
    const maxScrollDepthRef = useRef(0);

    const trackScrollDepth = useCallback(
        (currentDepth: number) => {
            try {
                analytics.trackEvent('page scrolled', {
                    ...eventProps,
                    depth: currentDepth,
                    // Maps to `action`
                    event: 'scrolled',
                    // Maps to `actionSubject`
                    eventComponent: 'page'
                });
            } catch (error) {
                sentryCaptureException({ exception: error });
            }
        },
        [eventProps]
    );

    const calculateScrollDepth = useCallback(() => {
        /**
         * Total scrollable height of the page content, including content not currently visible
         * This represents the full height from the top of the document to the bottom of all content
         * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight
         */
        const documentHeight = document.documentElement.scrollHeight;
        /**
         * Height of the browser's viewport (visible area)
         * This is the amount of content that can be seen without scrolling
         * Remains constant unless browser window is resized
         * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/innerHeight
         */
        const windowHeight = window.innerHeight;
        /**
         * Current scroll position from the top of the page in pixels
         * 0 means we're at the very top
         * Maximum value is documentHeight - windowHeight (when scrolled to bottom)
         * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/scrollY
         */
        const scrollY = window.scrollY;

        // scrollY + windowHeight gives us the bottom edge of the current viewport
        // Dividing by documentHeight gives us the percentage of the total document that's been viewed
        const currentDepth = Math.min(
            (scrollY + windowHeight) / documentHeight,
            1
        );

        // Only track if we've scrolled deeper than before
        if (currentDepth > maxScrollDepthRef.current) {
            maxScrollDepthRef.current = currentDepth;
            trackScrollDepth(currentDepth);
        }
    }, [trackScrollDepth]);

    useEffect(() => {
        // No need to run on server-side
        if (typeof window === 'undefined') {
            return;
        }

        const handleScroll = debounce(calculateScrollDepth, 250);

        // Calculate initial depth
        calculateScrollDepth();

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            handleScroll.cancel();
        };
    }, [calculateScrollDepth]);

    return null;
};
